import { useEffect, useMemo, useRef } from "react";
import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { C, R } from "../data/content";
import { HEADSHOT } from "../data/content";
import { Nav } from "../components/Nav";
import { Ticker } from "../components/Ticker";
import { Manifesto } from "../components/Manifesto";
import { Roadmap } from "../components/Audience";
import { Accordion } from "../components/Curriculum";
import { Footer, StickyCTA } from "../components/Footer";
import { ParticleHero } from "../components/ParticleHero";
import { VideoPlayer } from "../components/replay/VideoPlayer";
import { OfferStack, Guarantee, ReplayHost, Objections, ReplayProof, FinalBooking } from "../components/replay/ReplaySections";

gsap.registerPlugin(ScrollTrigger);

const ReplayHero = ({ watchPctRef }) => {
  const ref = useRef(null);
  useEffect(() => {
    const lines = ref.current.querySelectorAll(".mask-line > span");
    gsap.to(lines, { y: 0, duration: 1, ease: "power4.out", stagger: 0.12, delay: 0.35 });
    gsap.fromTo(ref.current.querySelectorAll(".hero-fade"),
      { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.9, stagger: 0.08, delay: 0.9, ease: "power2.out" });
  }, []);

  return (
    <section ref={ref} className="relative min-h-screen grid-bg overflow-hidden" data-testid="replay-hero-section" id="watch">
      <div className="absolute inset-y-0 right-0 w-1/2 hidden lg:block opacity-60"><ParticleHero /></div>
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 pt-32 pb-16 grid lg:grid-cols-[45fr_55fr] gap-12 items-center min-h-screen">
        <div className="relative z-10">
          <p className="mono text-[10px] tracking-[0.22em] text-[var(--accent)] mb-6 hero-fade" data-testid="replay-eyebrow">{R.eyebrow}</p>
          <h1 className="h1-display text-[clamp(36px,4.8vw,68px)]" data-testid="replay-h1">
            <span className="mask-line"><span>{R.h1.line1}</span></span>
            <span className="mask-line"><span className="text-[var(--accent)]">{R.h1.line2}</span></span>
            <span className="mask-line"><span className="opacity-60 text-[62%] leading-snug">{R.h1.line3}</span></span>
          </h1>
          <p className="text-[15px] text-white/55 leading-relaxed mt-7 max-w-[520px] hero-fade" data-testid="replay-subheadline">{R.sub}</p>
          <blockquote className="border-l-2 pl-5 italic text-[14px] text-white/60 mt-7 max-w-[480px] hero-fade" style={{ borderColor: "var(--accent)" }}>
            {R.blockquote}
          </blockquote>
          <p className="mono text-[11px] text-[var(--accent)] mt-7 hero-fade" data-testid="replay-availability-line">{R.replayLine}</p>
          <div className="flex items-center gap-4 mt-10 hero-fade">
            <img src={HEADSHOT} alt="Darren Gussick" className="w-[46px] h-[46px] rounded-full object-cover border-2" style={{ borderColor: "var(--accent)" }} />
            <div>
              <p className="text-sm font-bold">{C.hostName}</p>
              <p className="text-[11px] text-white/40">{C.hostCompany} · NMLS# {C.hostNmls}</p>
            </div>
          </div>
        </div>
        <div className="relative z-10 hero-fade">
          <VideoPlayer onMilestone={(m) => { watchPctRef.current = m; }} />
          <button className="btn-accent w-full cta mt-5" data-testid="replay-hero-cta"
            onClick={() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" })}>
            {R.cta}
          </button>
          <p className="text-[10px] text-white/25 text-center mt-3">{R.micro}</p>
        </div>
      </div>
    </section>
  );
};

export default function Replay() {
  const watchPctRef = useRef(0);

  const attribution = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get("utm_source"),
      utm_medium: p.get("utm_medium"),
      utm_campaign: p.get("utm_campaign"),
      utm_content: p.get("utm_content"),
      utm_term: p.get("utm_term"),
      fbclid: p.get("fbclid"),
      gclid: p.get("gclid"),
      pageUrl: window.location.href,
      referrer: document.referrer || null,
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    lenis.on("scroll", ScrollTrigger.update);
    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);
    const t = setTimeout(() => {
      gsap.utils.toArray("[data-reveal]").forEach((el) => {
        gsap.fromTo(el, { opacity: 0, y: 28 }, {
          opacity: 1, y: 0, duration: 0.8, ease: "power2.out",
          scrollTrigger: { trigger: el, start: "top 85%" },
        });
      });
      ScrollTrigger.refresh();
    }, 150);
    return () => {
      clearTimeout(t);
      gsap.ticker.remove(tick);
      lenis.destroy();
      ScrollTrigger.getAll().forEach((s) => s.kill());
    };
  }, []);

  return (
    <div className="relative" data-testid="replay-page">
      <Nav links={[["watch", "WATCH"], ["method", "THE METHOD"], ["host", "HOST"], ["faq", "FAQ"]]} ctaId="book" ctaLabel="BOOK MY REVIEW ↗" />
      <ReplayHero watchPctRef={watchPctRef} />
      <Ticker items={R.ticker} />
      <Manifesto label="[001] — WHAT YOU JUST LEARNED" lines={R.manifestoLines} paragraph={R.manifestoParagraph} ctaLabel="SEE MY EXACT NUMBER →" ctaTarget="book" />
      <div data-reveal><Roadmap items={R.roadmap} /></div>
      <div data-reveal><OfferStack /></div>
      <div data-reveal><Guarantee /></div>
      <div data-reveal><ReplayHost /></div>
      <div data-reveal><Objections /></div>
      <div data-reveal><ReplayProof /></div>
      <div data-reveal>
        <section id="faq" className="py-28 px-6 lg:px-12" data-testid="replay-faq-section">
          <div className="max-w-[900px] mx-auto">
            <p className="mono text-[11px] tracking-[0.25em] text-[var(--accent)] mb-6">[007] — GOOD TO KNOW</p>
            <h2 className="h1-display text-[clamp(28px,3.4vw,48px)] mb-12">COMMON QUESTIONS</h2>
            <Accordion items={R.faq.map((f) => ({ title: f.q, body: f.a }))} testPrefix="replay-faq" defaultOpen={-1} />
          </div>
        </section>
      </div>
      <div data-reveal><FinalBooking /></div>
      <Footer copyright={R.copyright} />
      <StickyCTA label={R.cta} target="book" />
    </div>
  );
}
